import React from 'react';
import { Box, Button, Chip, Typography } from '@mui/material';
import moment from 'moment-timezone';
import { actionDescriptionFromEvent } from '../../utils/actionDescription';

const DISPLAY_TZ = 'America/Chicago';

function isIdle(event: any): boolean {
  const type = String(event.event_type || '').toUpperCase();
  return type.includes('IDLE') || String(event.label || '').toLowerCase() === 'idle';
}

function rawActionLines(event: any): string[] {
  const detail = event.detail || {};
  return [
    event.event_type ? `event_type: ${event.event_type}` : null,
    event.source ? `source: ${event.source}` : null,
    detail.process_name ? `process: ${detail.process_name}` : null,
    detail.application_name ? `application: ${detail.application_name}` : null,
    detail.ui_element_type ? `ui_element_type: ${detail.ui_element_type}` : null,
    detail.ui_element_name ? `ui_element_name: ${detail.ui_element_name}` : null,
    detail.action_name ? `action_name: ${detail.action_name}` : null,
    detail.classification ? `classification: ${detail.classification}` : null,
    detail.monitor_id ? `monitor_id: ${detail.monitor_id}` : null,
  ].filter(Boolean) as string[];
}

export interface ComputerActivityTimelineProps {
  events: any[];
  total: number;
  showRawActions?: boolean;
  canViewSnapshots?: boolean;
  onViewSnapshot?: (event: any) => void;
}

const ComputerActivityTimeline: React.FC<ComputerActivityTimelineProps> = ({
  events,
  total,
  showRawActions = false,
  canViewSnapshots = false,
  onViewSnapshot,
}) => {
  if (!events.length) return null;

  return (
    <Box>
      {events.map((event, index) => {
        const idle = isIdle(event);
        const { label, context } = actionDescriptionFromEvent(event);
        const processName = event.detail?.process_name;
        const snapshotId = event.snapshot_id || event.detail?.snapshot_id;
        const hasSnapshot = Boolean(event.has_snapshot || snapshotId);
        const rawLines = rawActionLines(event);
        return (
          <Box
            key={`${event.source}-${event.id}`}
            sx={{
              display: 'grid',
              gridTemplateColumns: '110px 12px 1fr auto 72px',
              gap: 1.5,
              alignItems: 'flex-start',
              py: 1.25,
              px: 1,
              borderRadius: 1,
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums', pt: 0.25 }}>
              {moment(event.timestamp).tz(DISPLAY_TZ).format('h:mm:ss A')}
            </Typography>
            <Box sx={{ position: 'relative', height: '100%', minHeight: 36 }}>
              {index < events.length - 1 && (
                <Box sx={{
                  position: 'absolute',
                  left: 4,
                  top: 14,
                  bottom: -14,
                  width: 2,
                  bgcolor: idle ? 'grey.400' : 'warning.main',
                }}
                />
              )}
              <Box sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: idle ? 'grey.500' : 'warning.main',
                mt: 0.6,
              }}
              />
            </Box>
            <Box>
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <Typography variant="body2" fontWeight={700}>
                  {label}
                </Typography>
                {processName && (
                  <Chip size="small" label={processName} variant="outlined" sx={{ height: 20 }} />
                )}
                {event.source === 'session' && (
                  <Chip size="small" label={event.event_type} sx={{ height: 20 }} />
                )}
              </Box>
              {context ? (
                <Typography variant="caption" color="text.secondary" display="block">
                  {context}
                </Typography>
              ) : null}
              {showRawActions && rawLines.length > 0 && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ fontFamily: 'monospace', mt: 0.25 }}>
                  {rawLines.join(' · ')}
                </Typography>
              )}
            </Box>
            <Box sx={{ pt: 0.25 }}>
              {canViewSnapshots && hasSnapshot && onViewSnapshot ? (
                <Button size="small" variant="outlined" onClick={() => onViewSnapshot(event)}>
                  View Snapshot
                </Button>
              ) : null}
            </Box>
            <Typography variant="body2" color="text.secondary" align="right" sx={{ pt: 0.25 }}>
              {event.duration_label || '—'}
            </Typography>
          </Box>
        );
      })}
      <Typography variant="caption" color="text.secondary" display="block" textAlign="right" sx={{ mt: 1 }}>
        {total} events
      </Typography>
    </Box>
  );
};

export default ComputerActivityTimeline;
